import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Shield, 
  Feather, 
  Zap, 
  Clock, 
  TrendingDown,
  CheckCircle2,
  Building2,
  Lightbulb,
  Leaf,
  Award
} from "lucide-react";

const keyBenefits = [
  {
    icon: Shield,
    title: "Corrosion Resistance",
    description: "100% immunity to rust, chemicals, and saltwater environments. No degradation over time."
  },
  {
    icon: Feather,
    title: "Lightweight",
    description: "Up to 80% lighter than steel, reducing transportation costs and simplifying installation."
  },
  {
    icon: Zap,
    title: "High Strength",
    description: "2X stronger tensile strength compared to traditional TMT steel reinforcement bars."
  },
  {
    icon: Clock,
    title: "Longer Lifecycle",
    description: "100+ year design life with zero maintenance requirements in harsh conditions."
  },
  {
    icon: TrendingDown,
    title: "Cost Efficiency",
    description: "30-60% total project savings through reduced labor, transport, and maintenance costs."
  }
];

const blogContent = [
  {
    title: "The Steel Problem: Why Traditional Reinforcement is Failing",
    content: "For over a century, steel has been the backbone of construction. But in today's demanding environments—coastal structures, chemical plants, bridges, and tunnels—steel is showing its limitations. Corrosion alone costs the construction industry billions annually in repairs and replacements.",
    icon: Building2
  },
  {
    title: "Enter GFRP: The Next-Generation Solution",
    content: "Glass Fiber Reinforced Polymer (GFRP) represents a paradigm shift in construction materials. Engineered from high-strength glass fibers embedded in a polymer matrix, GFRP delivers superior performance where steel cannot compete.",
    icon: Lightbulb
  },
  {
    title: "Sustainability at the Core",
    content: "GFRP production has a significantly lower carbon footprint compared to steel manufacturing. With growing emphasis on green building certifications and sustainable construction practices, GFRP aligns perfectly with the future of eco-conscious infrastructure development.",
    icon: Leaf
  },
  {
    title: "Proven Performance Worldwide",
    content: "From bridges in North America to coastal infrastructure in the Middle East, GFRP has proven its worth in the most challenging environments. Leading government bodies including IRC, BIS, and various PWDs across India have now approved GFRP for critical infrastructure projects.",
    icon: Award
  }
];

const marketData = [
  { company: "Composite Building Innovations (CBI)", share: "22%", color: "bg-cyan-400" },
  { company: "Schoeck Group", share: "18%", color: "bg-red-500" },
  { company: "Armastek", share: "20%", color: "bg-yellow-400" },
  { company: "Dextra Composites", share: "10%", color: "bg-orange-500" },
  { company: "Pultron Composites", share: "8%", color: "bg-purple-600" },
  { company: "Other Regional Players", share: "22%", color: "bg-slate-700" }
];

export default function BlogPage() {
  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 py-20 md:py-28 lg:py-32" data-testid="blog-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80"></div>
        
        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-yellow-400 px-4 py-2 mb-6">
              <span className="text-slate-900 font-bold text-sm uppercase tracking-wider">Industry Insights</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Barlow_Condensed'] leading-tight mb-6">
              The Future of Construction:
              <span className="block text-yellow-400">Why GFRP is Replacing Steel</span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-3xl leading-relaxed">
              Discover how Glass Fiber Reinforced Polymer technology is revolutionizing infrastructure 
              with unmatched <span className="text-white font-semibold">innovation</span>, 
              <span className="text-white font-semibold"> sustainability</span>, and 
              <span className="text-white font-semibold"> durability</span>—building structures that last 100+ years.
            </p>
            
            <Link to="/products">
              <Button 
                className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base"
                data-testid="blog-hero-cta"
              >
                Explore Solutions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Blog Content Section */}
      <section className="section-spacing bg-white" data-testid="blog-content">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="accent-line mx-auto mb-6"></div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Barlow_Condensed']">
                Understanding the GFRP Revolution
              </h2>
              <p className="text-slate-600 mt-4 text-lg max-w-2xl mx-auto">
                A comprehensive look at why the construction industry is shifting towards advanced composite materials
              </p>
            </div>

            <div className="space-y-12">
              {blogContent.map((section, index) => (
                <article 
                  key={index}
                  className="flex gap-6 lg:gap-8 items-start"
                  data-testid={`blog-section-${index}`}
                >
                  <div className="flex-shrink-0 w-14 h-14 lg:w-16 lg:h-16 bg-yellow-400 flex items-center justify-center">
                    <section.icon className="w-7 h-7 lg:w-8 lg:h-8 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-3">
                      {section.title}
                    </h3>
                    <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            {/* Key Statistics */}
            <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: "100+", label: "Year Lifespan" },
                { value: "80%", label: "Weight Reduction" },
                { value: "2X", label: "Stronger Than Steel" },
                { value: "0%", label: "Corrosion Rate" }
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="text-center p-6 bg-slate-50 border border-slate-200"
                >
                  <div className="text-3xl lg:text-4xl font-bold text-yellow-500 font-['Barlow_Condensed']">
                    {stat.value}
                  </div>
                  <div className="text-slate-600 text-sm lg:text-base mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="section-spacing bg-slate-900 relative noise-overlay" data-testid="blog-benefits">
        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="accent-line mx-auto mb-6"></div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Barlow_Condensed']">
              Key Benefits of GFRP Technology
            </h2>
            <p className="text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
              Why leading infrastructure developers and engineers are choosing GFRP over traditional steel
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {keyBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-8 hover:border-yellow-400 transition-all duration-300 group"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 bg-yellow-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-7 h-7 text-slate-900" />
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-white font-['Barlow_Condensed'] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-400 text-base leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Insights Section */}
      <section className="section-spacing bg-slate-50" data-testid="blog-insights">
        <div className="section-container">
          <div className="text-center mb-16">
            <div className="accent-line mx-auto mb-6"></div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Barlow_Condensed']">
              Global GFRP Market Trends
            </h2>
            <p className="text-slate-600 mt-4 text-lg max-w-2xl mx-auto">
              The composite rebar market is experiencing rapid growth as construction shifts towards sustainable solutions
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Market Overview */}
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
                Market Leaders 2022-2028
              </h3>
              
              <div className="space-y-4">
                {marketData.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className={`w-5 h-5 ${item.color} flex-shrink-0`}></div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-700 font-medium text-base lg:text-lg">{item.company}</span>
                        <span className="text-slate-900 font-bold text-lg lg:text-xl">{item.share}</span>
                      </div>
                      <div className="w-full bg-slate-200 h-2 mt-2 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${item.color} rounded-full`}
                          style={{ width: item.share }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-slate-500 text-sm mt-6">
                Data Source: Global Information Premium Market Research, 2022-2028
              </p>
            </div>

            {/* Key Insights */}
            <div className="bg-white p-8 lg:p-10 border border-slate-200 shadow-lg">
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
                Key Market Insights
              </h3>
              
              <ul className="space-y-4">
                {[
                  "Global GFRP rebar market projected to grow at 12.5% CAGR through 2028",
                  "Asia-Pacific region leading adoption with India as a key emerging market",
                  "Infrastructure projects driving 65% of total GFRP demand",
                  "Government mandates accelerating GFRP adoption in coastal and corrosive environments",
                  "Sustainability requirements pushing construction firms toward composite solutions"
                ].map((insight, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-base lg:text-lg">{insight}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Projected Market Value by 2028</p>
                    <p className="text-3xl lg:text-4xl font-bold text-slate-900 font-['Barlow_Condensed']">$2.8 Billion</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-sm">Annual Growth Rate</p>
                    <p className="text-3xl lg:text-4xl font-bold text-green-500 font-['Barlow_Condensed']">+12.5%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="py-16 bg-white" data-testid="related-topics">
        <div className="section-container">
          <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-8">
            Explore More Topics
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "GFRP vs Steel: A Technical Comparison",
                description: "Detailed analysis of mechanical properties, durability, and lifecycle costs.",
                link: "/why-gfrp"
              },
              {
                title: "Applications Across Industries",
                description: "Discover 24+ application areas from bridges to industrial flooring.",
                link: "/applications"
              },
              {
                title: "Our Product Range",
                description: "Explore PRO BAR and PRO MESH solutions for your projects.",
                link: "/products"
              }
            ].map((topic, index) => (
              <Link 
                key={index}
                to={topic.link}
                className="group p-6 bg-slate-50 border border-slate-200 hover:border-yellow-400 transition-all duration-300"
              >
                <h4 className="text-lg lg:text-xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-2 group-hover:text-yellow-600 transition-colors">
                  {topic.title}
                </h4>
                <p className="text-slate-600 text-base mb-4">{topic.description}</p>
                <span className="inline-flex items-center text-yellow-600 font-semibold text-sm uppercase tracking-wide">
                  Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="section-spacing bg-yellow-400" data-testid="blog-cta">
        <div className="section-container text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
            Partner with BuildEdge Tech
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto mb-10 text-lg lg:text-xl">
            Join the construction revolution. Get technical specifications, project consultation, 
            and expert guidance for implementing GFRP solutions in your next project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                className="bg-slate-900 text-white hover:bg-slate-800 font-bold uppercase tracking-wider rounded-none px-10 py-6 text-base"
                data-testid="blog-contact-cta"
              >
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919871070356">
              <Button 
                variant="outline"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold uppercase tracking-wider rounded-none px-10 py-6 text-base"
              >
                Call +91-9871070356
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
